// Scenario picker for the (preview) screen-simulation harness.
// Scenarios are grouped by their `group` field and rendered in registry order,
// so the elimination flow (eliminate → fortify → place) reads top-to-bottom.
import { Link } from 'expo-router'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { SCENARIOS, type PreviewScenario } from '../../../preview/scenarios'

function groupScenarios(scenarios: PreviewScenario[]): [string, PreviewScenario[]][] {
  const order: string[] = []
  const byGroup = new Map<string, PreviewScenario[]>()
  for (const s of scenarios) {
    if (!byGroup.has(s.group)) {
      byGroup.set(s.group, [])
      order.push(s.group)
    }
    byGroup.get(s.group)!.push(s)
  }
  return order.map((g) => [g, byGroup.get(g)!])
}

export default function PreviewIndex() {
  const groups = groupScenarios(SCENARIOS)
  return (
    <ScrollView contentContainerStyle={{ padding: 16, gap: 12 }}>
      <Text style={{ fontSize: 20, fontWeight: '700', marginBottom: 4 }}>
        Screen previews
      </Text>
      <Text style={{ color: '#666', marginBottom: 8 }}>
        Fixture-driven renders of real game screens — no DB, no network. Dev only.
      </Text>
      {groups.map(([group, scenarios]) => (
        <View key={group} style={{ gap: 10 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: '#999',
              marginTop: 8,
            }}
          >
            {group}
          </Text>
          {scenarios.map((s) => (
            <Link key={s.id} href={`/preview/${s.id}`} asChild>
              <Pressable
                style={{
                  padding: 14,
                  borderWidth: 1,
                  borderColor: '#d4d4d4',
                  borderRadius: 12,
                  gap: 4,
                }}
              >
                <Text style={{ fontWeight: '600', fontSize: 15 }}>{s.title}</Text>
                <Text style={{ color: '#666', fontSize: 13 }}>{s.description}</Text>
              </Pressable>
            </Link>
          ))}
        </View>
      ))}

      <Link href="/preview/attack-log-concepts" asChild>
        <Pressable style={{ padding: 14, borderWidth: 1, borderColor: '#738348', borderRadius: 12 }}>
          <Text style={{ fontWeight: '600' }}>Attack summary · 12 concepts</Text>
        </Pressable>
      </Link>
      {/* UX concepts — focused demos of in-flight interaction changes. */}
      <Link href="/preview/turn-panel-concepts-round-four" asChild>
        <Pressable accessibilityRole="button" accessibilityLabel="Turn area concepts, fourth round" style={{ padding: 14, borderWidth: 1, borderColor: '#d4d4d4', borderRadius: 12, gap: 4 }}>
          <Text style={{ fontWeight: '600', fontSize: 15 }}>Turn area — round four</Text>
          <Text style={{ color: '#666', fontSize: 13 }}>Eight options around Open well and Floating controls, including two Firm well variations with roomier placement controls.</Text>
        </Pressable>
      </Link>
      <Link href="/preview/turn-panel-concepts-round-three" asChild>
        <Pressable accessibilityRole="button" accessibilityLabel="Turn area concepts, third round" style={{ padding: 14, borderWidth: 1, borderColor: '#d4d4d4', borderRadius: 12, gap: 4 }}>
          <Text style={{ fontWeight: '600', fontSize: 15 }}>Turn area — round three</Text>
          <Text style={{ color: '#666', fontSize: 13 }}>Eleven options: quiet edges, paper lines outside the controls and stronger raised boxes. Play through a turn in full screen.</Text>
        </Pressable>
      </Link>
      <Link href="/preview/turn-panel-concepts-round-two" asChild>
        <Pressable accessibilityRole="button" accessibilityLabel="Turn area concepts, second round" style={{ padding: 14, borderWidth: 1, borderColor: '#d4d4d4', borderRadius: 12, gap: 4 }}>
          <Text style={{ fontWeight: '600', fontSize: 15 }}>Turn area — round two</Text>
          <Text style={{ color: '#666', fontSize: 13 }}>Inset well and Field notes, plus four variations. Play a full turn in every option.</Text>
        </Pressable>
      </Link>
      <Link href="/preview/turn-panel-concepts" asChild>
        <Pressable accessibilityRole="button" accessibilityLabel="Turn area concepts" style={{ padding: 14, borderWidth: 1, borderColor: '#d4d4d4', borderRadius: 12, gap: 4 }}>
          <Text style={{ fontWeight: '600', fontSize: 15 }}>Turn area — six concepts</Text>
          <Text style={{ color: '#666', fontSize: 13 }}>Compare panel treatments, then open a full game at phone or desktop size.</Text>
        </Pressable>
      </Link>
      <View style={{ gap: 10 }}>
        <Text
          style={{
            fontSize: 12,
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: '#999',
            marginTop: 8,
          }}
        >
          UX concepts
        </Text>
        <Link href="/preview/map-controls" asChild>
          <Pressable
            style={{
              padding: 14,
              borderWidth: 1,
              borderColor: '#d4d4d4',
              borderRadius: 12,
              gap: 4,
            }}
          >
            <Text style={{ fontWeight: '600', fontSize: 15 }}>Map controls — button bar</Text>
            <Text style={{ color: '#666', fontSize: 13 }}>
              Zoom −/+ and a labelled view toggle in a bar beneath the map, replacing the on-map
              corner chips. Zoom is always on. Works portrait + landscape.
            </Text>
          </Pressable>
        </Link>
        <Link href="/preview/fonts" asChild>
          <Pressable
            style={{
              padding: 14,
              borderWidth: 1,
              borderColor: '#d4d4d4',
              borderRadius: 12,
              gap: 4,
            }}
          >
            <Text style={{ fontWeight: '600', fontSize: 15 }}>Body font — candidates</Text>
            <Text style={{ color: '#666', fontSize: 13 }}>
              Sample in-game copy rendered in the current body font plus candidate replacements,
              for small-screen readability comparison (web).
            </Text>
          </Pressable>
        </Link>
      </View>
    </ScrollView>
  )
}
